import { useCallback, useMemo, useState } from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { DataGrid, GridActionsCellItem, GridColDef, GridPaginationModel, GridRowParams } from '@mui/x-data-grid';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import { downloadIssuedForm } from 'api/hooks/private/forms/DownloadIssuedForm';
import { useEmailForm } from 'api/hooks/private/forms/EmailForm';
import { getIssuedFormLink } from 'api/hooks/private/forms/GetIssuedFormLink';
import { useIssueIndianaCDEForm } from 'api/hooks/private/forms/IssueIndianaCDEForm';
import { StudentIssuedForm, useGetStudentIssuedForms } from 'api/hooks/private/users/students/GetStudentIssuedForms';

import SelectCourseEnrollmentDialog from 'components/Dialogs/Courses/SelectCourseEnrollmentDialog';
import SendEmailDialog from 'components/Dialogs/Email/SendEmailDialog';
import FeedbackDialog from 'components/Dialogs/FeedbackDialog';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

import FormsToolbar from './FormsToolbar';

dayjs.extend(localizedFormat);
dayjs.extend(calendar);

interface FormsProps {
  studentId: string;
}

export function Forms({ studentId }: FormsProps) {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 25,
    page: 0,
  });
  const queryClient = useQueryClient();
  const forms = useGetStudentIssuedForms({
    variables: {
      id: studentId,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    },
    // use previous data as placeholder to maintain a stable
    // reference while fetching the next page so the datagrid
    // doesn't reset the pagination model
    placeholderData: keepPreviousData,
  });

  const issueCDEForm = useIssueIndianaCDEForm();
  const emailForm = useEmailForm();

  const dialogs = useDialogs();

  const handleViewForm = useCallback(
    async (id: string) => {
      try {
        await getIssuedFormLink({
          id: id,
          action: 'new_tab',
        });
      } catch (error) {
        dialogs.open(FeedbackDialog, {
          title: 'Error Opening Form',
          message: getApiErrorMessage(error),
        });
      }
    },
    [dialogs],
  );

  const handleEmailForm = useCallback(
    async (issuedForm: StudentIssuedForm) => {
      try {
        dialogs.open(SendEmailDialog, {
          title: `Send "${issuedForm.form.name}"`,
          templateKey: 'form-issued',
          id: issuedForm.form.id,
          onSubmit: async (data) => {
            console.log(JSON.stringify(data, null, 2));
            // dialogs.alert(JSON.stringify(data, null, 2));

            try {
              await emailForm.mutateAsync({
                issuedFormId: issuedForm.id,
                templateOverride: data,
              });
              dialogs.open(FeedbackDialog, {
                title: 'Emailed Form',
                icon: 'success',
              });
            } catch (error) {
              dialogs.open(FeedbackDialog, {
                title: 'Error Opening Form',
                message: getApiErrorMessage(error),
              });
            }
            return new Promise((resolve) => setTimeout(resolve, 1000));
          },
        });
      } catch (error) {
        dialogs.open(FeedbackDialog, {
          title: 'Error Opening Form',
          message: getApiErrorMessage(error),
        });
      }
    },
    [dialogs, emailForm],
  );

  const handleDownloadForm = useCallback(
    async (id: string) => {
      try {
        await downloadIssuedForm({
          id: id,
        });
      } catch (error) {
        dialogs.open(FeedbackDialog, {
          title: 'Error Downloading Form',
          message: getApiErrorMessage(error),
        });
      }
    },
    [dialogs],
  );

  const handleIssueCDEForm = useCallback(async () => {
    try {
      const courseEnrollmentIds = await dialogs.open(SelectCourseEnrollmentDialog, {
        studentId: studentId,
        selectionsRequired: 1,
      });

      // ensure that the user has selected a course enrollment
      if (!courseEnrollmentIds || courseEnrollmentIds.length === 0) {
        dialogs.open(FeedbackDialog, {
          title: 'No Course Enrollments Selected',
          message: 'Please select a course enrollment to issue the CDE form.',
        });
        return;
      }

      console.log(`Selected Course Enrollments: ${courseEnrollmentIds}`);
      await issueCDEForm.mutateAsync({
        courseEnrollmentId: courseEnrollmentIds[0],
      });

      queryClient.invalidateQueries({ queryKey: ['student_issued_forms'] });
      dialogs.open(FeedbackDialog, {
        icon: 'success',
        title: 'CDE Form Issued',
        message: 'The CDE form has been issued successfully.',
      });
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error Issuing CDE Form',
        message: getApiErrorMessage(error),
      });
    }
  }, [dialogs, issueCDEForm, queryClient, studentId]);

  const getFormActions = useCallback(
    (params: GridRowParams<StudentIssuedForm>) => {
      // check if the form is expired
      const expires = params.row.expires;
      const isExpired = !!expires && dayjs(expires).isBefore(dayjs());
      return [
        <Tooltip title="Open in new tab" key={params.id}>
          <GridActionsCellItem
            component="span"
            icon={<OpenInNewIcon />}
            label="Open in new tab"
            onClick={() => handleViewForm(params.id as string)}
            disabled={isExpired}
          />
        </Tooltip>,
        <Tooltip title="Email" key={params.id}>
          <GridActionsCellItem
            component="span"
            icon={<EmailIcon />}
            label="Email"
            onClick={() => handleEmailForm(params.row)}
            disabled={isExpired}
          />
        </Tooltip>,
        <Tooltip title="Download" key={params.id}>
          <GridActionsCellItem
            component="span"
            icon={<DownloadIcon />}
            label="Download"
            onClick={() => handleDownloadForm(params.id as string)}
            disabled={isExpired}
          />
        </Tooltip>,
      ];
    },
    [handleDownloadForm, handleEmailForm, handleViewForm],
  );

  const columns: GridColDef<StudentIssuedForm>[] = useMemo(
    () => [
      {
        field: 'form',
        headerName: 'Form Name',
        type: 'string',
        sortable: false,
        flex: 1,
        valueGetter: (_value, row) => {
          return row?.form?.name ?? '';
        },
      },
      {
        field: 'issued_at',
        headerName: 'Date Issued',
        type: 'dateTime',
        sortable: true,
        width: 250,
        valueFormatter: (value) =>
          (value && dayjs(value).calendar(null, { sameElse: 'MMM D, YYYY [at] h:mm a' })) || null,
      },
      {
        field: 'issued_by_user',
        headerName: 'Issued By',
        type: 'dateTime',
        sortable: false,
        width: 150,
        valueFormatter: (value, row) => {
          if (value) {
            return `${row?.issued_by_user?.nickname ?? row?.issued_by_user?.first_name ?? ''} ${row?.issued_by_user?.last_name ?? ''}`;
          }
          return null;
        },
      },
      {
        field: 'expires',
        headerName: 'Expires',
        type: 'string',
        sortable: false,
        width: 250,
        cellClassName: (params) => {
          const expires = params.row.expires;
          const isExpired = expires && dayjs(expires).isBefore(dayjs());

          // if form is expired add the expired css class
          if (isExpired) {
            return 'expired';
          }

          return '';
        },
        valueFormatter: (value) => (value && dayjs(value).format('ll')) || 'No Expiration',
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        flex: 1,
        getActions: getFormActions,
      },
    ],
    [getFormActions],
  );

  return (
    <>
      <DataGrid<StudentIssuedForm>
        columns={columns}
        rows={forms.data?.rows}
        loading={forms.isFetching}
        disableVirtualization // disable virtualization since we will only have 100 rows per page at most
        showToolbar
        slots={{
          toolbar: FormsToolbar,
        }}
        slotProps={{
          toolbar: {
            issueFormMenuItems: [
              {
                label: 'Issue CDE Form',
                onClick: handleIssueCDEForm,
              },
            ],
          },
        }}
        // enable serverside pagination
        paginationMode="server"
        rowCount={forms.data?.count ?? 0}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        disableColumnFilter
        disableColumnSorting
        sx={{
          '& .expired': {
            color: (theme) => theme.palette.error.main,
          },
        }}
      />
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={issueCDEForm.isPending}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}

export default Forms;
